import {
  CloseOutlined,
  LoadingOutlined,
  MenuOutlined,
  MoonOutlined,
  SearchOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { css } from '@linaria/core';
import { Link } from '@tanstack/react-router';
import { Button, Flex, Layout, Spin } from 'antd';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import DataBoundary from '@components/data-boundary';
import { Account } from '@lib/types';
import { ROUTE_PATHS } from '@modules/route-paths';

import BrandLogo from '../brand-logo';
import CartButton from './components/cart-button';
import GlobalCardSearch from './components/global-card-search';
import HeaderNavigation from './components/header-navigation';
import MobileNavigation from './components/mobile-navigation';
import OrderNotifications from './components/order-notifications';
import UserDropdown from './components/user-dropdown';
import renderSearchError from './render-search-error';
import { useAppTheme } from '../../config/theme/provider';

const styles = {
  header: css`
    position: relative;
    height: 64px;
    padding-inline: 0;
    line-height: normal;
    border-bottom: 1px solid var(--ant-color-border-secondary);
    background: var(--ant-color-bg-container);
    @media (max-width: 575px) {
      height: 56px;
    }
  `,
  content: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ant-margin-sm);
    width: 100%;
    max-width: 1600px;
    height: 100%;
    margin-inline: auto;
    padding-inline: var(--ant-padding-lg);
    box-sizing: border-box;
    > * {
      flex-shrink: 0;
    }
    @media (max-width: 1199px) {
      gap: var(--ant-margin-xs);
    }
    @media (max-width: 575px) {
      padding-inline: var(--ant-padding-sm);
    }
  `,
  links: css`
    margin-left: var(--ant-margin);

    @media (max-width: 991px) {
      margin-left: 0px;
      display: none;
    }
  `,
  menu: css`
    @media (min-width: 992px) {
      display: none;
    }
  `,
  search: css`
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    max-width: 420px;
    margin-right: var(--ant-margin);

    @media (max-width: 1599px) {
      max-width: 340px;
    }
    @media (max-width: 1050px) {
      display: none;
    }
  `,
  expandedSearch: css`
    @media (max-width: 1050px) {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: var(--ant-margin-xs);
      position: absolute;
      top: 100%;
      left: 0;
      z-index: 10;
      width: 100%;
      max-width: none;
      padding: var(--ant-padding-sm);
      box-sizing: border-box;
      background: var(--ant-color-bg-container);
      border-bottom: 1px solid var(--ant-color-border-secondary);
    }
  `,
  searchToggle: css`
    margin-left: auto;
    @media (min-width: 1051px) {
      display: none;
    }
  `,
  closeSearch: css`
    @media (min-width: 1051px) {
      display: none;
    }
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--ant-margin-xs);
    .ant-btn {
      min-width: 40px;
      height: 40px;
      border-radius: var(--ant-border-radius);
    }
    .ant-btn-icon {
      font-size: 20px;
      color: var(--ant-color-text-secondary);
    }
    @media (max-width: 575px) {
      gap: var(--ant-margin-xxs);
    }
  `,
  leftSpace: css`
    align-self: center;
  `,
};

interface AppHeaderProps {
  account?: Account | null;
  accountLoading?: boolean;
}

const AppHeader = ({ account, accountLoading = false }: AppHeaderProps) => {
  const { mode, toggleTheme } = useAppTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchButton = useRef<HTMLButtonElement>(null);

  const handleCloseSearch = () => {
    setSearchOpen(false);
    if (searchOpen) {
      searchButton.current?.focus();
    }
  };

  useEffect(() => {
    if (!searchOpen) {
      return undefined;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSearchOpen(false);
        searchButton.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [searchOpen]);

  return (
    <Layout.Header className={styles.header}>
      <div className={styles.content}>
        <Flex gap={8} align="center">
          <Button
            className={styles.menu}
            type="text"
            icon={<MenuOutlined />}
            aria-label="Abrir menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          />
          <BrandLogo />
          <div className={styles.links}>
            <HeaderNavigation />
          </div>
        </Flex>
        <div className={styles.actions}>
          <div
            className={classNames(
              styles.search,
              searchOpen && styles.expandedSearch,
            )}
          >
            <DataBoundary
              loadingFallback={
                <Spin
                  indicator={<LoadingOutlined spin />}
                  size="small"
                  aria-label="Carregando busca"
                />
              }
              errorFallback={renderSearchError}
            >
              <GlobalCardSearch
                expanded={searchOpen}
                onNavigate={handleCloseSearch}
              />
            </DataBoundary>
            {searchOpen ? (
              <Button
                className={styles.closeSearch}
                type="text"
                icon={<CloseOutlined />}
                aria-label="Fechar busca"
                onClick={handleCloseSearch}
              />
            ) : null}
          </div>
          <Button
            ref={searchButton}
            className={styles.searchToggle}
            type="text"
            icon={<SearchOutlined />}
            aria-label="Abrir busca"
            aria-expanded={searchOpen}
            onClick={() => setSearchOpen(!searchOpen)}
          />
          <Flex gap={8}>
            {account?.verifiedAt ? (
              <>
                <DataBoundary>
                  <CartButton accountId={account.id} />
                </DataBoundary>
                <DataBoundary>
                  <OrderNotifications />
                </DataBoundary>
              </>
            ) : null}
            {account ? (
              <div className={styles.leftSpace}>
                <DataBoundary>
                  <UserDropdown hideName />
                </DataBoundary>
              </div>
            ) : accountLoading ? (
              <Spin
                indicator={<LoadingOutlined spin />}
                aria-label="Carregando conta"
              />
            ) : (
              <>
                <Button
                  type="text"
                  aria-label={
                    mode === 'light'
                      ? 'Ativar tema escuro'
                      : 'Ativar tema claro'
                  }
                  icon={mode === 'light' ? <SunOutlined /> : <MoonOutlined />}
                  onClick={toggleTheme}
                />
                <Link to={ROUTE_PATHS.LOGIN}>
                  <Button type="text">Entrar</Button>
                </Link>
              </>
            )}
          </Flex>
        </div>
      </div>
      <MobileNavigation open={menuOpen} onClose={() => setMenuOpen(false)} />
    </Layout.Header>
  );
};

export default AppHeader;
